import React, { useState, useEffect, useCallback } from "react";
import {
  Shield, User, Lock, Unlock, Check, X, AlertTriangle,
  RefreshCw, Eye, Save, ChevronDown, ChevronUp,
  Mail, MessageCircle, MessageSquare, Phone, FileText, Calendar, DollarSign,
  Users, Folder, Edit3, CheckCircle, Clock, Database, Target, BarChart2,
  BarChart, BookOpen, Briefcase, Cpu, CreditCard, Clipboard, Wrench,
  Send, Headphones, Inbox, Brain, Smartphone, Table2, HardDrive,
  TrendingUp, Video, Mic, Cloud, Receipt, Landmark, Palette,
  PenLine, Stamp, Settings, GitBranch
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const CATEGORY_ICONS = {
  client_info: User, client_status: FileText, client_history: Clock, all_clients: Users,
  emails: Mail, sms: MessageCircle, whatsapp: MessageSquare, calls: Phone,
  call_transcriptions: Headphones, connect_messages: MessageCircle, comments: Edit3,
  pending_exchanges: Inbox, rdv: Calendar, leads: Target, pipeline_stats: BarChart2,
  pipeline_config: Settings, pipeline_workflows: GitBranch,
  eligibilites: CheckCircle, financier: DollarSign, pennylane_bank: CreditCard,
  factures: FileText, devis: Clipboard, travaux: Wrench, documents: Folder,
  call_analyses: BarChart, formations: BookOpen, direction_comite: Briefcase,
  ai_usage: Cpu, ia_agents: Database, security: Shield, jobs_cron: RefreshCw,
  write_emails: Mail, write_comments: Edit3, modify_status: RefreshCw,
  create_rdv: Calendar, send_sms: Send, send_whatsapp: Send,
  app_emergent_llm: Brain, app_ringover: Phone, app_twilio_sms: MessageCircle,
  app_whatsapp_business: MessageSquare, app_whatsapp_baileys: Smartphone,
  app_google_sheets: Table2, app_google_drive: HardDrive, app_google_ads: TrendingUp,
  app_gmail_imap: Mail, app_canva: Palette, app_mongodb: Database,
  app_api_gouv: Landmark, app_sora2: Video, app_whisper: Mic, app_wttr: Cloud,
  app_pennylane: Receipt, app_stripe: CreditCard, app_ringover_sms: MessageCircle,
};

const DOMAIN_LABELS = {
  client: "Donnees Client", communication: "Communication Multicanal",
  planning: "RDV & Planning", commercial: "Pipeline & Commercial",
  finance: "Finance & Banque", technique: "Travaux & Technique",
  documents: "Documents", formation: "Formation & Coaching",
  direction: "Direction & IA", systeme: "Securite & Systeme",
  integrations: "Apps & Integrations"
};

const DOMAIN_COLORS = {
  client: "text-blue-400", communication: "text-green-400", planning: "text-cyan-400",
  commercial: "text-orange-400", finance: "text-red-400", technique: "text-yellow-400",
  documents: "text-indigo-400", formation: "text-pink-400", direction: "text-purple-400",
  systeme: "text-slate-400", integrations: "text-teal-400"
};

const RISK_COLORS = {
  low: { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
  medium: { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" },
  high: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" }
};

/* ── Tiny toggle pill for R / W / V ── */
const LevelToggle = ({ active, icon: Icon, label, color, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    title={label}
    className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all border ${
      active
        ? `${color} border-current/30`
        : "text-slate-600 border-slate-700/50 hover:border-slate-500"
    }`}
  >
    <Icon className="w-3 h-3" />
    <span className="hidden lg:inline">{label}</span>
  </button>
);

export default function IAPermissionsDashboard() {
  const [directors, setDirectors] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedDirector, setExpandedDirector] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [directorsRes, categoriesRes] = await Promise.all([
        authFetch(`${API}/api/ia-permissions/directors`),
        authFetch(`${API}/api/ia-permissions/categories`)
      ]);
      if (directorsRes.ok && categoriesRes.ok) {
        const dd = await directorsRes.json();
        const cc = await categoriesRes.json();
        setDirectors(dd.directors || []);
        setCategories(cc.categories || {});
      }
    } catch (e) {
      console.error("Erreur chargement données:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* Get current permissions (edited or original) */
  const getPerms = (director) =>
    editedPermissions[director.director_id] || director.data_permissions || {};

  const hasChanges = (directorId, original) => {
    const edited = editedPermissions[directorId];
    if (!edited) return false;
    return JSON.stringify(edited) !== JSON.stringify(original || {});
  };

  /* Toggle a specific level for a permission */
  const toggleLevel = (directorId, permKey, level, currentPerms) => {
    const perms = { ...(editedPermissions[directorId] || currentPerms || {}) };
    const cur = perms[permKey] || { read: false, write: false, visa: false };
    const newVal = !cur[level];
    const updated = { ...cur };

    if (level === "read" && !newVal) {
      updated.read = false; updated.write = false; updated.visa = false;
    } else if (level === "write" && newVal) {
      updated.read = true; updated.write = true;
    } else if (level === "visa" && newVal) {
      updated.read = true; updated.visa = true;
    } else {
      updated[level] = newVal;
    }

    if (!updated.read && !updated.write && !updated.visa) {
      const { [permKey]: _, ...rest } = perms;
      setEditedPermissions(prev => ({ ...prev, [directorId]: rest }));
    } else {
      perms[permKey] = updated;
      setEditedPermissions(prev => ({ ...prev, [directorId]: perms }));
    }
  };

  /* Bulk actions */
  const grantAll = (directorId) => {
    const perms = {};
    Object.keys(categories).forEach(k => { perms[k] = { read: true, write: true, visa: false }; });
    setEditedPermissions(prev => ({ ...prev, [directorId]: perms }));
  };
  const revokeAll = (directorId) => {
    setEditedPermissions(prev => ({ ...prev, [directorId]: {} }));
  };

  const savePermissions = async (directorId) => {
    if (!editedPermissions[directorId]) return;
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/api/ia-permissions/director/${directorId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissions: editedPermissions[directorId], description: "Permissions modifiées" })
      });
      if (resp.ok) {
        loadData();
        setEditedPermissions(prev => { const next = { ...prev }; delete next[directorId]; return next; });
      }
    } catch (e) { console.error("Erreur sauvegarde:", e); }
    setSaving(false);
  };

  const countActive = (perms) => Object.values(perms).filter(v => v?.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="permissions-loading">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
        <span className="ml-3 text-slate-400">Chargement des permissions IA...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ia-permissions-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Permissions des Directeurs IA</h2>
            <p className="text-sm text-slate-400">Lecture / Ecriture / Visa IA par agent</p>
          </div>
        </div>
        <button onClick={loadData} data-testid="refresh-permissions-btn" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm">
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700 text-xs" data-testid="legend-bar">
        <span className="text-slate-400 font-medium">Legende :</span>
        <span className="flex items-center gap-1 text-blue-400"><Eye className="w-3.5 h-3.5" /> Lecture</span>
        <span className="flex items-center gap-1 text-amber-400"><PenLine className="w-3.5 h-3.5" /> Ecriture</span>
        <span className="flex items-center gap-1 text-purple-400"><Stamp className="w-3.5 h-3.5" /> Visa IA</span>
        <span className="mx-2 text-slate-600">|</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400"></span> Faible</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"></span> Moyen</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Eleve</span>
        <div className="flex-1" />
        <span className="text-slate-500"><Database className="w-3.5 h-3.5 inline mr-1" />{Object.keys(categories).length} categories</span>
      </div>

      {/* Directors list */}
      <div className="space-y-3" data-testid="directors-list">
        {directors.map(director => {
          const perms = getPerms(director);
          const isExpanded = expandedDirector === director.director_id;
          const changed = hasChanges(director.director_id, director.data_permissions);

          return (
            <div key={director.director_id} data-testid={`director-${director.director_id}`} className={`bg-slate-900/50 rounded-xl border transition-all ${changed ? "border-purple-500/50" : "border-slate-700"}`}>
              {/* Director header */}
              <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-800/30" onClick={() => setExpandedDirector(isExpanded ? null : director.director_id)}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: `${director.color || '#6b7280'}20` }}>
                  {director.avatar || ""}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{director.name}</h3>
                    <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-400">{director.pole || director.director_id}</span>
                    {changed && <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs flex items-center gap-1"><Edit3 className="w-3 h-3" /> Modifie</span>}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5 truncate">{director.description?.substring(0, 80)}</p>
                </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-emerald-400">{countActive(perms)} / {Object.keys(categories).length}</p>
                                    <p className="text-xs text-slate-500">actives</p>
                                  </div>
                                  {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                                </div>
              </div>

              {/* Expanded panel */}
              {isExpanded && (
                <div className="border-t border-slate-700 p-4 space-y-5">
                  {/* Quick actions */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => grantAll(director.director_id)} data-testid={`grant-all-${director.director_id}`} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm flex items-center gap-2"><Unlock className="w-4 h-4" /> Tout autoriser</button>
                    <button onClick={() => revokeAll(director.director_id)} data-testid={`revoke-all-${director.director_id}`} className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-lg text-sm flex items-center gap-2"><Lock className="w-4 h-4" /> Tout revoquer</button>
                    <div className="flex-1" />
                    {changed && (
                      <button onClick={() => savePermissions(director.director_id)} disabled={saving} data-testid={`save-${director.director_id}`} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg flex items-center gap-2">
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Sauvegarder
                      </button>
                    )}
                  </div>

                  {/* Permission grid grouped by domain */}
                  {Object.entries(
                    Object.entries(categories).reduce((acc, [key, cat]) => {
                      const d = cat.domain || "autre";
                      if (!acc[d]) acc[d] = [];
                      acc[d].push([key, cat]);
                      return acc;
                    }, {})
                  ).map(([domain, cats]) => {
                    const domainActive = cats.filter(([k]) => perms[k]?.read).length;
                    return (
                      <div key={domain} data-testid={`domain-${domain}`}>
                        <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${DOMAIN_COLORS[domain] || "text-slate-400"}`}>
                          {DOMAIN_LABELS[domain] || domain} ({domainActive}/{cats.length})
                        </h4>
                        <div className="space-y-1">
                          {cats.map(([key, cat]) => {
                            const perm = perms[key] || { read: false, write: false, visa: false };
                            const Icon = CATEGORY_ICONS[key] || Shield;
                            const riskColors = RISK_COLORS[cat.risk_level] || RISK_COLORS.low;
                            return (
                              <div key={key} data-testid={`perm-row-${key}`} className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${perm.read ? `${riskColors.bg} ${riskColors.border}` : "bg-slate-800/20 border-slate-700/40"}`}>
                                <Icon className={`w-4 h-4 flex-shrink-0 ${perm.read ? riskColors.text : "text-slate-600"}`} />
                                <div className="flex-1 min-w-0">
                                  <span className={`text-sm font-medium ${perm.read ? "text-white" : "text-slate-500"}`}>{cat.label}</span>
                                  {cat.has_action && <span className="text-[10px] text-amber-500/70 ml-2">{cat.action_label}</span>}
                                  <span className="text-[10px] text-slate-500 ml-2 hidden md:inline">{cat.description}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <LevelToggle
                                    active={perm.read}
                                    icon={Eye}
                                    label="Lecture"
                                    color="text-blue-400"
                                    testId={`perm-${key}-read`}
                                    onClick={() => toggleLevel(director.director_id, key, "read", director.data_permissions)}
                                  />
                                  <LevelToggle
                                    active={perm.write}
                                    icon={PenLine}
                                    label="Ecriture"
                                    color="text-amber-400"
                                    testId={`perm-${key}-write`}
                                    onClick={() => toggleLevel(director.director_id, key, "write", director.data_permissions)}
                                  />
                                  <LevelToggle
                                    active={perm.visa}
                                    icon={Stamp}
                                    label="Visa"
                                    color="text-purple-400"
                                    testId={`perm-${key}-visa`}
                                    onClick={() => toggleLevel(director.director_id, key, "visa", director.data_permissions)}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {directors.length === 0 && (
        <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-700" data-testid="no-directors">
          <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Aucun directeur IA configure</p>
        </div>
      )}
    </div>
  );
}
